import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { clsx } from 'clsx';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { gridEndFor, YEARS_PER_GRID } from './historyQuery';

/** Colonnes de la grille (4 rangées de 3) */
const COLUMNS = 3;

/**
 * Choix de la vue de l'Historique (« year picker », demande du chef de projet, 2026-10-15) : un bouton
 * « 12 derniers mois ▾ » (value null) ou « 2025 ▾ ✕ », qui ouvre une grille de 12 années.
 *  - les ‹ › de la grille changent de douzaine d'années, sans limite vers le passé ; années futures (> max) désactivées
 *  - choisir une année ferme la grille ; « 12 derniers mois », en bas de la grille, ou la croix ramènent à la vue par défaut
 *  - clavier : flèches dans la grille (← → d'une année, ↑ ↓ d'une rangée, en changeant de douzaine au bord), Entrée choisit,
 *    Échap ferme (Radix)
 * Largeur fixe : la croix est incrustée dans le bouton. Pas de borne basse (une année sans période l'indique d'elle-même).
 */
export function YearPicker({
  value,
  max,
  onChange,
}: {
  value: number | null;
  max: number;
  onChange: (year: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [gridEnd, setGridEnd] = useState(max);
  const [focused, setFocused] = useState(max);
  const grid = useRef<HTMLDivElement>(null);
  // Focus à déplacer après le rendu (la case visée peut être dans une autre douzaine, pas encore affichée)
  const focusPending = useRef(false);

  useEffect(() => {
    if (!focusPending.current) return;
    focusPending.current = false;
    grid.current?.querySelector<HTMLButtonElement>(`[data-year="${focused}"]`)?.focus();
  }, [focused, gridEnd, open]);
  const years = Array.from({ length: YEARS_PER_GRID }, (_, i) => gridEnd - YEARS_PER_GRID + 1 + i);

  const openAt = (next: boolean) => {
    if (next) {
      const year = value ?? max;
      setGridEnd(gridEndFor(year, max));
      setFocused(year);
      // À l'ouverture : focus sur l'année choisie (ou l'année en cours), comme les sélecteurs standard
      focusPending.current = true;
    }
    setOpen(next);
  };

  const choose = (year: number | null) => {
    onChange(year);
    setOpen(false);
  };

  /** Déplace le focus clavier, en changeant de douzaine au bord de la grille */
  const moveFocus = (year: number) => {
    if (year > max) return;
    focusPending.current = true;
    setGridEnd(gridEndFor(year, max));
    setFocused(year);
  };

  const onGridKeyDown = (e: KeyboardEvent) => {
    const moves: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -COLUMNS, ArrowDown: COLUMNS };
    if (e.key in moves) {
      e.preventDefault();
      moveFocus(focused + moves[e.key]);
    }
  };

  const arrow =
    'p-1.5 rounded-md text-slate-500 hover:text-blue-700 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent';

  return (
    <Popover open={open} onOpenChange={openAt}>
      {/* Largeur fixe, celle de « 12 derniers mois » : la croix est incrustée, la largeur ne bouge pas */}
      <div className="relative w-[10rem]">
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`Période affichée : ${value ?? '12 derniers mois'}`}
            className={clsx(
              'w-full flex items-center justify-between gap-2 pl-3 pr-2 py-2 border rounded-lg shadow-sm text-sm font-medium tabular-nums transition-colors',
              value === null
                ? 'bg-card border-slate-300 text-slate-700 hover:bg-slate-50'
                : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100',
            )}
          >
            <span data-testid="year-filter">{value ?? '12 derniers mois'}</span>
            <ChevronDown className="w-4 h-4 shrink-0" />
          </button>
        </PopoverTrigger>
        {value !== null && (
          <button
            type="button"
            aria-label="Revenir aux 12 derniers mois"
            title="Revenir aux 12 derniers mois"
            onClick={() => onChange(null)}
            className="absolute right-8 top-1/2 -translate-y-1/2 p-1 rounded-full text-blue-700 hover:bg-blue-200 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <PopoverContent
        align="end"
        className="w-64 p-3"
        aria-label="Choisir une année"
        // Focus sur l'année visée plutôt que sur la fenêtre ; aussi posé par l'effet ci-dessus, selon que la grille est
        // montée avant (navigateur) ou après (jsdom) cet événement
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          grid.current?.querySelector<HTMLButtonElement>(`[data-year="${focused}"]`)?.focus();
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <button
            type="button"
            aria-label="Années précédentes"
            onClick={() => setGridEnd(gridEnd - YEARS_PER_GRID)}
            className={arrow}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium text-slate-700 tabular-nums">
            {years[0]} – {gridEnd}
          </span>
          <button
            type="button"
            aria-label="Années suivantes"
            disabled={gridEnd >= max}
            onClick={() => setGridEnd(Math.min(max, gridEnd + YEARS_PER_GRID))}
            className={arrow}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div ref={grid} role="group" aria-label="Années" className="grid grid-cols-3 gap-1" onKeyDown={onGridKeyDown}>
          {years.map((year) => (
            <button
              key={year}
              type="button"
              data-year={year}
              aria-pressed={year === value}
              disabled={year > max}
              tabIndex={year === focused ? 0 : -1}
              onFocus={() => setFocused(year)}
              onClick={() => choose(year)}
              className={clsx(
                'py-2 rounded-md text-sm tabular-nums transition-colors disabled:text-slate-300 disabled:hover:bg-transparent',
                year === value
                  ? 'bg-blue-600 text-white dark:text-background hover:bg-blue-700'
                  : 'text-slate-700 hover:bg-slate-100',
                year === max && year !== value && 'font-semibold',
              )}
            >
              {year}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => choose(null)}
          aria-pressed={value === null}
          className={clsx(
            'mt-2 w-full py-2 rounded-md text-sm border-t border-slate-100 transition-colors',
            value === null ? 'text-blue-700 font-medium' : 'text-slate-600 hover:bg-slate-50',
          )}
        >
          12 derniers mois
        </button>
      </PopoverContent>
    </Popover>
  );
}
